import React, { useEffect, useRef, useState } from "react";
import Colors from "../../../../Helper/ColorsConstant";

// const cardsData = [
//     {
//         id: 1,
//         title: "Rebrand and Branding Guidelines",
//         owner: "Jason C",
//         percent: "82.9%",
//         description:
//             "Generate revised set of branding guidelines (and logo) for Nowcomm",
//     },
//     {
//         id: 2,
//         title: "Rebrand and Branding Guidelines",
//         owner: "Jason C",
//         percent: "82.9%",
//         description: "Second card content...",
//     },
//     {
//         id: 3,
//         title: "Rebrand and Branding Guidelines",
//         owner: "Jason C",
//         percent: "82.9%",
//         description: "Third card content...",
//     },
//     {
//         id: 4,
//         title: "Rebrand and Branding Guidelines",
//         owner: "Jason C",
//         percent: "82.9%",
//         description: "Third card content...",
//     },
//     {
//         id: 5,
//         title: "Rebrand and Branding Guidelines",
//         owner: "Jason C",
//         percent: "82.9%",
//         description: "Third card content...",
//     },
// ];

export default function WidgetCards({ cardsData = [] }) {
    const parentRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const dataVersionRef = useRef(0);  // Track data version to prevent stale state restoration
    const isCalculatingRef = useRef(false);  // Track if calculation is in progress
    const colors = Colors;
    const normalizedCardsData = Array.isArray(cardsData) ? cardsData : [];

    const [cardStates, setCardStates] = useState(
        normalizedCardsData.map((_, index) => ({
            isExpanded: index === 0,
            isHalfOpen: false,
            fullHeight: 0,  // Store full height for accurate half-open calculation
        }))
    );
    const [isReady, setIsReady] = useState(false);  // Hide cards until first calc completes
    const hasInitiallyLoadedRef = useRef(false);  // Track if we've loaded at least once
    const [imageErrors, setImageErrors] = useState({});  // Track image load errors by card id

    useEffect(() => {
        // Increment data version to invalidate any pending auto-reverts
        dataVersionRef.current += 1;

        initialStateRef.current = null;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        cardRefs.current = new Array(normalizedCardsData.length);

        // Clear any pending auto-revert timer when data changes
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        // Reset image errors when data changes
        setImageErrors({});

        if (normalizedCardsData.length === 0) {
            setCardStates([]);
            setIsReady(true);
            hasInitiallyLoadedRef.current = true;
            return;
        }

        // Only hide on initial load, not on data updates (to prevent flickering)
        if (!hasInitiallyLoadedRef.current) {
            setIsReady(false);
            setCardStates(
                normalizedCardsData.map((_, index) => ({
                    isExpanded: false,
                    isHalfOpen: false,
                    fullHeight: 0,
                }))
            );
        } else {
            // On data update, mark as calculating to prevent style application
            isCalculatingRef.current = true;
            // Preserve states temporarily, calculation will update them
            setCardStates((prevStates) => {
                const newLength = normalizedCardsData.length;
                if (prevStates.length === newLength) {
                    return prevStates; // Keep existing states to avoid flicker
                }
                // Adjust length but keep as many existing states as possible
                const newStates = normalizedCardsData.map((_, index) =>
                    prevStates[index] || {
                        isExpanded: false,
                        isHalfOpen: false,
                        fullHeight: 0,
                    }
                );
                return newStates;
            });
        }

        const timer = setTimeout(() => {
            calculateAndAdjustHeight();
        }, 50);  // Minimal delay to ensure DOM is updated

        return () => {
            clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
        };
    }, [normalizedCardsData]);

    useEffect(() => {
        // Wait longer for DOM to be fully rendered with all styles applied and images loaded
        const timer = setTimeout(() => {
            calcHandlerRef.current?.();
        }, 500);  // Increased from 300ms to 500ms

        // Also recalculate on images load since avatar affects height
        const images = document.querySelectorAll(".card img");
        const handleImageLoad = () => calcHandlerRef.current?.();
        images.forEach((img) => {
            img.addEventListener("load", handleImageLoad);
        });

        // Simple window resize handler with debounce
        const handleResize = () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            resizeDebounceRef.current = setTimeout(() => {
                calcHandlerRef.current?.();
                resizeDebounceRef.current = null;
            }, 300);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            clearTimeout(timer);
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            window.removeEventListener("resize", handleResize);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
            });
            // Clean up auto-revert timer on unmount
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
        };
    }, []);

    // Apply styles whenever cardStates changes
    useEffect(() => {
        // Only apply styles after initial calculation completes and not during recalculation
        if (!isReady || isCalculatingRef.current) return;

        console.log("Applying styles based on cardStates change...", normalizedCardsData);
        applyStyles();
        // Don't update lastCalcTimeRef here - only calculateAndAdjustHeight should do that
    }, [cardStates, isReady]);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    const calculateAndAdjustHeight = () => {
        if (normalizedCardsData.length === 0) {
            setIsReady(true);
            return;
        }

        // Allow first calculation to always run, then throttle subsequent ones
        const now = Date.now();
        if (!isFirstCalculationRef.current && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false; // Mark first calculation as done
        lastCalcTimeRef.current = now;

        const parentHeight = parentRef.current.clientHeight;
        const cardElements = cardRefs.current.slice(0, normalizedCardsData.length);

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        // Temporarily expand all cards to measure their true heights
        const originalHeights = cardElements.map((card) => {
            const body = card.querySelector(".card-body");
            if (body) {
                const original = {
                    maxHeight: body.style.maxHeight,
                    display: body.style.display,
                    overflow: body.style.overflow,
                    padding: body.style.padding,
                    margin: body.style.margin
                };
                body.style.maxHeight = "none";
                body.style.display = "block";  // Make visible for measurement (critical!)
                body.style.overflow = "visible";
                body.style.padding = "";  // Reset to allow natural padding
                body.style.margin = "";   // Reset to allow natural margin
                return { card, body, original };
            }
            return null;
        });

        // Wait a tick for layout to recalculate
        requestAnimationFrame(() => {
            const headerHeights = cardElements.map((card) => {
                const header = card.querySelector(".card-header");
                return header ? header.offsetHeight : 35;
            });
            const cardSpacing = cardElements.map((card) => {
                const styles = window.getComputedStyle(card);
                const marginBottom = parseFloat(styles.marginBottom || "0");
                return Number.isFinite(marginBottom) ? marginBottom : 0;
            });
            const sumCollapsedHeaders = (count) => {
                let sum = 0;
                for (let idx = 0; idx < count; idx++) {
                    sum += headerHeights[idx] + cardSpacing[idx];
                }
                return sum;
            };

            // Measure all card body heights
            const cardHeights = cardElements.map((card) => {
                const body = card.querySelector(".card-body");
                return body ? body.scrollHeight : 0;
            });

            // Restore original display state
            originalHeights.forEach((item) => {
                if (item) {
                    item.body.style.maxHeight = item.original.maxHeight;
                    item.body.style.display = item.original.display;
                    item.body.style.overflow = item.original.overflow;
                    item.body.style.padding = item.original.padding;
                    item.body.style.margin = item.original.margin;
                }
            });

            // Calculate layout - use the scrollable container's height
            const scrollContainer = parentRef.current.querySelector(".slim-scroll");
            const availableHeight = scrollContainer ? scrollContainer.clientHeight : parentHeight;

            const newCardStates = normalizedCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,  // Store full height
            }));

            // If only one card, expand it to fill the entire container
            if (cardElements.length === 1) {
                newCardStates[0].isExpanded = true;
            } else {
                // Multiple cards: Fit maximum full cards, then ONE half-open, rest collapsed
                let remainingHeight = availableHeight;
                let expandedCount = 0;

                // Phase 1: Expand cards from bottom to top (full body)
                for (let i = cardElements.length - 1; i >= 0; i--) {
                    const cardFullHeight = headerHeights[i] + cardHeights[i] + cardSpacing[i];

                    // Check if we can fit this full card AND leave room for remaining card headers
                    const cardsAbove = i; // Number of cards that need to be collapsed above this one
                    const minSpaceForAboveHeaders = cardsAbove > 0 ? sumCollapsedHeaders(cardsAbove) : 0;

                    if (remainingHeight >= cardFullHeight + minSpaceForAboveHeaders) {
                        newCardStates[i].isExpanded = true;
                        remainingHeight -= cardFullHeight;
                        expandedCount++;
                    } else {
                        break;  // Can't fit more full cards, stop here
                    }
                }

                // Phase 2: Try to add ONE half-open card
                // Do this whether or not we have full cards - always try to show at least some content
                if (expandedCount === 0) {
                    // No full cards fit - try to show half-open from the last card
                    const lastIndex = cardElements.length - 1;
                    const halfCardHeight = headerHeights[lastIndex] + Math.ceil(cardHeights[lastIndex] / 2) + cardSpacing[lastIndex];
                    const minSpaceForAbove = lastIndex > 0 ? sumCollapsedHeaders(lastIndex) : 0;

                    if (remainingHeight >= halfCardHeight + minSpaceForAbove) {
                        newCardStates[lastIndex].isHalfOpen = true;
                        remainingHeight -= halfCardHeight;
                    }
                } else {
                    // We have full cards - place ONE half-open card directly above the topmost full card
                    const topExpandedIndex = newCardStates.findIndex((state) => state.isExpanded);
                    const halfIndex = topExpandedIndex > 0 ? topExpandedIndex - 1 : -1;

                    if (halfIndex >= 0) {
                        const halfCardHeight = headerHeights[halfIndex] + Math.ceil(cardHeights[halfIndex] / 2) + cardSpacing[halfIndex];
                        const cardsAbove = halfIndex;
                        const minSpaceForAbove = cardsAbove > 0 ? sumCollapsedHeaders(cardsAbove) : 0;

                        if (remainingHeight >= halfCardHeight + minSpaceForAbove) {
                            newCardStates[halfIndex].isHalfOpen = true;
                            remainingHeight -= halfCardHeight;
                        }
                    }
                }
            }

            // Mark calculation as complete BEFORE setting states to allow applyStyles to run
            isCalculatingRef.current = false;

            setCardStates(newCardStates);
            calcRetryRef.current = 0;

            // Store the initial calculated state for auto-revert with data version
            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),  // Deep copy
                    dataVersion: dataVersionRef.current
                };
            }

            // Always set ready after calculation completes
            setIsReady(true);
            hasInitiallyLoadedRef.current = true;
        });
    };

    const applyStyles = () => {
        cardRefs.current.forEach((cardEl, index) => {
            if (!cardEl) return;

            const body = cardEl.querySelector(".card-body");
            if (!body) return;

            if (cardStates[index]?.isExpanded) {
                body.style.maxHeight = "none";
                body.style.overflow = "visible";
                body.style.padding = "";
                body.style.margin = "";
                body.style.display = "block";
            } else if (cardStates[index]?.isHalfOpen) {
                // Use stored fullHeight from calculation for accurate half-open (50% of content)
                const fullHeight = cardStates[index].fullHeight;
                if (fullHeight > 0) {
                    body.style.maxHeight = fullHeight / 2 + "px";
                    body.style.overflow = "hidden";
                    body.style.padding = "0.5rem";
                    body.style.margin = "0";
                    body.style.display = "block";
                }
            } else {
                body.style.maxHeight = "0px";
                body.style.overflow = "hidden";
                body.style.padding = "0";
                body.style.margin = "0";
                body.style.display = "none";
            }
        });
    };

    const toggleCard = (index) => {
        setCardStates((prevStates) => {
            const newStates = [...prevStates];
            newStates[index].isExpanded = !newStates[index].isExpanded;
            newStates[index].isHalfOpen = false;
            return newStates;
        });

        // Clear any existing timer
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        // Set new timer to revert after 5 seconds (for both expand and collapse)
        const currentVersion = dataVersionRef.current;
        autoRevertTimerRef.current = setTimeout(() => {
            // Only restore if data hasn't changed
            if (initialStateRef.current &&
                initialStateRef.current.dataVersion === currentVersion &&
                initialStateRef.current.states.length === cardStates.length) {
                const restoredState = JSON.parse(JSON.stringify(initialStateRef.current.states));  // Deep copy
                setCardStates(restoredState);
            }
            autoRevertTimerRef.current = null;
        }, 20000);
    };

    const formatOwnerName = (name) => {
        if (!name) return "";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0];

        // First word as is, rest as initials
        const formatted = [words[0]];
        for (let i = 1; i < words.length; i++) {
            formatted.push(words[i][0].toUpperCase());
        }
        return formatted.join(" ");
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0][0]?.toUpperCase() || "?";
        // First letter of first word + first letter of last word
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const handleImageError = (cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    };

    return (
        <div ref={parentRef} style={{ height: "100%", width: "100%" }}>
            <div
                className="slim-scroll"
                style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    opacity: isReady ? 1 : 0,
                    visibility: isReady ? "visible" : "hidden",
                    transition: "opacity 0.2s ease-in",
                }}
            >
                <div className="border" style={{ padding: "3px" }}>
                    {normalizedCardsData.map((card, index) => (
                        <div
                            className="card mb-1"
                            key={card.id}
                            ref={(el) => (cardRefs.current[index] = el)}
                        >
                            <div
                                className="card-header fw-bold"
                                style={{
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    backgroundColor: colors[index % colors.length],
                                    color: "#ffffff",
                                    borderRadius: "0",
                                }}
                                // title={card.title}  // Tooltip shows full title on hover
                                onClick={() => toggleCard(index)}
                            >
                                {/* {card.title} */}
                            </div>

                            <div
                                className="card-body collapse show"
                                style={{
                                    overflow: "hidden",
                                    transition: "max-height 0.3s ease-in-out",
                                    maxHeight: "none",
                                }}
                            >
                                <div className="row align-items-center">
                                    <div className="col text-start">
                                        <div
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "120px",
                                            }}
                                            title={card.topLeftText}
                                        >
                                            {card.topLeftText}
                                        </div>
                                    </div>
                                    <div className="col text-center">
                                        <div>Status</div>
                                        <div className="fs-4 fw-bold">{card.percent}</div>
                                    </div>
                                    <div className="col d-flex flex-column align-items-end">
                                        <div style={{ width: "80px", display: "flex", justifyContent: "center" }}>
                                            {!card.imageUrl || imageErrors[card.id] ? (
                                                <div
                                                    style={{
                                                        width: 50,
                                                        height: 50,
                                                        borderRadius: "50%",
                                                        backgroundColor: colors[0] || "#5f8b95",
                                                        color: "white",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "18px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {getInitials(card.owner)}
                                                </div>
                                            ) : (
                                                <img
                                                    src={card.imageUrl}
                                                    alt="avatar"
                                                    style={{
                                                        width: 50,
                                                        height: 50,
                                                        borderRadius: "50%",
                                                    }}
                                                    onError={() => handleImageError(card.id)}
                                                />
                                            )}
                                        </div>

                                        <div
                                            style={{
                                                width: "80px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                textAlign: "center",
                                            }}
                                            title={card.owner}
                                        >
                                            {formatOwnerName(card.owner)}
                                        </div>
                                    </div>
                                </div>

                                <p
                                    className="mt-3 fs-4 fw-bold text-center"
                                    style={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                    title={card.title}
                                >
                                    {card.title}
                                </p>
                                <p
                                    className="mt-3 text-muted text-center"
                                    style={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                    title={card.description}
                                >
                                    {card.description}
                                </p>

                                <div className="row  small">
                                    <div className="col">
                                        <b>Opened</b>
                                        <br />
                                        {card.openDate}
                                    </div>
                                    <div className="col text-center">
                                        <b>Promised</b>
                                        <br />
                                        {card.promiseDate}
                                    </div>
                                    <div className="col d-flex flex-column align-items-end">
                                        <div style={{
                                            width: "80px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            textAlign: "center",
                                        }}>
                                            <b>{card.status}</b>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
